import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, Users, Facebook, CheckCircle2, Clock } from "lucide-react";

/**
 * Admin page — Facebook Ads Custom Audience export.
 *
 * Downloads every buyer email (Stripe payments + legacy free-forever members)
 * in the Facebook-compatible CSV format:
 *   email,first_name,last_name,phone,country
 *
 * Auto-syncs from Stripe every 24 hours; admin can also trigger a manual full
 * backfill at any time.
 *
 * Endpoints used:
 *   GET  /api/admin/buyers/stats
 *   POST /api/admin/buyers/sync?full=true|false
 *   GET  /api/admin/buyers/export.csv
 */
export default function AdminBuyers() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refreshStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/buyers/stats");
      setStats(data);
    } catch {
      toast.error("تعذّر تحميل الإحصائيات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshStats(); }, []);
  // If a sync started elsewhere (e.g. nightly job), reflect the in-progress
  // state in the UI when this page mounts.
  useEffect(() => {
    if (stats?.in_progress && !syncing) setSyncing(true);
    // eslint-disable-next-line
  }, [stats?.in_progress]);

  const runSync = async (full = false) => {
    setSyncing(true);
    const t = toast.loading(full ? "جاري بدء السحب الكامل من Stripe..." : "جاري بدء تحديث القائمة من Stripe...");
    try {
      const { data } = await api.post(`/admin/buyers/sync${full ? "?full=true" : ""}`);
      toast.success(
        data.message || "بدأت المزامنة في الخلفية — حدّث الصفحة بعد قليل.",
        { id: t, duration: 7000 },
      );
      // Poll stats every 3s while in_progress so the UI updates automatically.
      const poll = setInterval(async () => {
        try {
          const { data: s } = await api.get("/admin/buyers/stats");
          setStats(s);
          if (!s.in_progress) {
            clearInterval(poll);
            setSyncing(false);
            if (s.last_sync_result) {
              toast.success(`تمت المزامنة — فُحص ${s.last_sync_result.scanned} · أُثري ${s.last_sync_result.enriched} · جديد ${s.last_sync_result.orphans}`, { duration: 6000 });
            }
          }
        } catch { /* keep polling */ }
      }, 3000);
      // Safety: stop polling after 10 minutes no matter what.
      setTimeout(() => { clearInterval(poll); setSyncing(false); }, 10 * 60 * 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت المزامنة", { id: t });
      setSyncing(false);
    }
  };

  const downloadCsv = async () => {
    const t = toast.loading("جاري إنشاء ملف CSV...");
    try {
      const res = await api.get("/admin/buyers/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `faisalfit_buyers_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("تم تنزيل ملف CSV — جاهز لرفعه في Facebook Ads", { id: t, duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التنزيل", { id: t });
    }
  };

  const lastSyncStr = stats?.last_sync_at
    ? new Date(stats.last_sync_at).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" })
    : "لم تُشغَّل بعد";

  return (
    <div className="space-y-6" dir="rtl" data-testid="admin-buyers-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Facebook className="h-6 w-6 text-blue-600" />
          قائمة عملاء Facebook Ads
        </h1>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          حمّل كل عملائك في ملف CSV واحد جاهز لإنشاء Custom Audience في فيسبوك.
          يشمل الملف: عملاء Stripe (بجميع إيميلاتهم — إيميل النموذج، Apple Pay، التسجيل) + الأعضاء "مجاني للأبد".
          <br />
          <span className="text-xs">الملف يُحدَّث تلقائيًا كل 24 ساعة من Stripe.</span>
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          label="مشترين Stripe"
          value={loading ? "…" : (stats?.stripe_paid_purchases ?? 0)}
          icon={<Users className="h-5 w-5 text-emerald-600" />}
        />
        <StatCard
          label="أعضاء مجاني للأبد"
          value={loading ? "…" : (stats?.legacy_free_forever ?? 0)}
          icon={<Users className="h-5 w-5 text-amber-600" />}
        />
        <StatCard
          label="مشتريات Stripe غير مرتبطة"
          value={loading ? "…" : (stats?.stripe_orphan_buyers ?? 0)}
          icon={<Users className="h-5 w-5 text-sky-600" />}
        />
      </div>

      {/* Download card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4 text-primary" />
            تنزيل الملف
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={downloadCsv}
            size="lg"
            className="w-full h-16 text-base gap-3"
            data-testid="btn-download-buyers-csv"
          >
            <Download className="h-6 w-6" />
            <div className="text-right">
              <div className="font-bold">تنزيل CSV لـ Facebook Ads</div>
              <div className="text-xs opacity-80 font-normal">email, first_name, last_name, phone, country</div>
            </div>
          </Button>
          <div className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-md p-3">
            <strong>كيفية الاستخدام في فيسبوك:</strong>
            <ol className="list-decimal ps-4 mt-1.5 space-y-1">
              <li>افتح Meta Business Suite → Audiences → Create Audience → Custom Audience</li>
              <li>اختر "Customer list" → ارفع هذا الملف</li>
              <li>فيسبوك يطابق الإيميلات ويبني الجمهور خلال 30-60 دقيقة</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Sync card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-primary" />
            المزامنة مع Stripe
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">آخر مزامنة:</span>
            <span className="font-medium">{lastSyncStr}</span>
            {stats?.last_sync_at && (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" title="نجحت آخر مزامنة" />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => runSync(false)}
              disabled={syncing}
              variant="secondary"
              className="gap-2"
              data-testid="btn-sync-incremental"
            >
              {syncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              تحديث سريع
            </Button>
            <Button
              onClick={() => runSync(true)}
              disabled={syncing}
              variant="outline"
              className="gap-2"
              data-testid="btn-sync-full"
            >
              {syncing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              سحب كامل من Stripe (كل التاريخ)
            </Button>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>تحديث سريع:</strong> يجيب فقط المدفوعات الجديدة منذ آخر مزامنة (سريع، ثوانٍ).
            <br />
            <strong>سحب كامل:</strong> يمر على كل مدفوعاتك في Stripe من أول يوم (استخدمه مرة واحدة عند البدء أو للتحقق).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-2xl font-bold mt-0.5">{value}</div>
        </div>
        {icon}
      </CardContent>
    </Card>
  );
}
